import type { ReactNode } from 'react';

import { Autocomplete, Chip, TextField } from '@mui/material';

import { FieldRow } from './FieldRow';

export interface ChipListFieldProps {
  label: ReactNode;
  description?: ReactNode;
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Optional known suggestions; the field still accepts free-text entries. */
  options?: string[];
}

/**
 * Free-text list editor: type a value and press Enter to add a chip. Used for
 * the many `string[]` settings (required fields, restrictions, steps, statuses…).
 */
export function ChipListField({
  label,
  description,
  value,
  onChange,
  disabled = false,
  placeholder = 'Type and press Enter…',
  options = [],
}: ChipListFieldProps) {
  const items = value ?? [];
  return (
    <FieldRow label={label} description={description} alignTop>
      <Autocomplete
        multiple
        freeSolo
        disabled={disabled}
        options={options}
        value={items}
        onChange={(_event, next) => onChange(next as string[])}
        renderTags={(tags, getTagProps) =>
          tags.map((tag, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return <Chip size="small" label={tag} key={key} {...tagProps} />;
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder={items.length === 0 ? placeholder : undefined}
            aria-label={typeof label === 'string' ? label : undefined}
          />
        )}
      />
    </FieldRow>
  );
}
